import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { getAuth } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../Config/config";

const PersonalDetails = () => {
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(1);
  const [maxMembers, setMaxMembers] = useState(3);
  const [people, setPeople] = useState([]);
  const [nominees, setNominees] = useState([{ name: "" }]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const packageDetails = location.state?.packageDetails;
  const basePrice = parseFloat(packageDetails?.price?.replace(/,/g, "") || 0);
  const maxLocations = packageDetails?.duration === "3 Years" ? 3 : 5;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationsCollection = collection(db, "locations");
        const locationsSnapshot = await getDocs(locationsCollection);
        const locationsList = locationsSnapshot.docs.map((doc) => ({
          ...doc.data(),
          locId: doc.id,
        }));
        setLocations(locationsList);
        setAvailableLocations(locationsList);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    // Update maxMembers to include nominees
    setMaxMembers(adults + kids + nominees.length);
  }, [adults, kids, nominees.length]);

  useEffect(() => {
    if (!packageDetails && location.state?.returnUrl) {
      navigate(location.state.returnUrl);
      return;
    }
    if (!packageDetails) {
      navigate("/");
      return;
    }

    const newPeople = Array(adults + kids)
      .fill()
      .map(() => ({
        name: "",
        dob: "",
        type: "",
      }));
    setPeople(newPeople);
  }, [adults, kids, packageDetails, location.state?.returnUrl, navigate]);

  useEffect(() => {
    const priceForAdults = basePrice * (0.5 * (adults - 2));
    const priceForKids = kids > 0 ? basePrice * (0.25 * (kids - 1)) : 0;
    const calculatedPrice = basePrice + priceForAdults + priceForKids;

    const formattedPrice = calculatedPrice.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });

    setTotalPrice(formattedPrice);
  }, [adults, kids, basePrice]);

  const validateForm = () => {
    const newErrors = {};

    people.forEach((person, index) => {
      if (!person.name.trim()) {
        newErrors[`person${index}`] = "Name is required";
      }
      if (!person.dob) {
        newErrors[`personDob${index}`] = "Date of birth is required";
      }
    });

    nominees.forEach((nominee, index) => {
      if (!nominee.name.trim()) {
        newErrors[`nominee${index}`] = "Nominee name is required";
      }
    });

    if (selectedLocations.length === 0) {
      newErrors.locations = "Please select at least one location";
    }

    if (selectedLocations.length > maxLocations) {
      newErrors.locations = `Maximum ${maxLocations} locations allowed for this package`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLocationSelect = (event) => {
    const selectedLocation = locations.find(
      (loc) => loc.locId === event.target.value
    );

    if (selectedLocation && selectedLocations.length < maxLocations) {
      setSelectedLocations([...selectedLocations, selectedLocation]);
      setAvailableLocations(
        availableLocations.filter((loc) => loc.locId !== selectedLocation.locId)
      );
    }
  };

  const removeLocation = (locId) => {
    const removedLocation = locations.find((loc) => loc.locId === locId);
    setSelectedLocations(
      selectedLocations.filter((loc) => loc.locId !== locId)
    );
    setAvailableLocations([...availableLocations, removedLocation]);
  };

  const increaseCount = (type) => {
    if (type === "adults") setAdults(adults + 1);
    else if (type === "kids") setKids(kids + 1);
    else if (type === "nominees" && nominees.length < 3) {
      setNominees([...nominees, { name: "" }]);
    }
  };

  const decreaseCount = (type) => {
    if (type === "adults" && adults > 2) setAdults(adults - 1);
    else if (type === "kids" && kids > 0) setKids(kids - 1);
    else if (type === "nominees" && nominees.length > 1) {
      setNominees(nominees.slice(0, -1));
    }
  };

  const handleDateChange = (index, date) => {
    const updatedPeople = [...people];
    updatedPeople[index].dob = date;
    setPeople(updatedPeople);
  };

  const handleProceed = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      navigate("/login", {
        state: {
          from: "/details",
          packageDetails,
          returnUrl: location.pathname,
        },
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    const locationData = selectedLocations.map((loc) => loc.location);
    const numericPrice = parseFloat(totalPrice.replace(/,/g, ""));

    // Include nominees in maxMembers calculation
    const totalMaxMembers = adults + kids + nominees.length;

    navigate("/purchase", {
      state: {
        packageDetails,
        personalDetails: {
          people: people.map((person, index) => ({
            ...person,
            type: index < adults ? "adult" : "kid",
          })),
          nominees: nominees.filter((nominee) => nominee.name.trim() !== ""),
          adults,
          kids,
          maxMembers: totalMaxMembers,
          totalPrice: numericPrice,
          selectedLocations: locationData,
        },
      },
    });
  };

  return (
    <Container>
      <ScrollContainer>
        <ContentWrapper>
          <PriceCard>
            <PriceTitle>Package Price: ₹{totalPrice}</PriceTitle>
            <PriceSubtext>
              Price for {adults} adults{kids > 0 ? ` and ${kids} kids` : ""}
            </PriceSubtext>
            <MaxMembersText>
              Total Members: {maxMembers} (Including {nominees.length} nominee
              {nominees.length !== 1 ? "s" : ""})
            </MaxMembersText>
          </PriceCard>

          <Card>
            <CardTitle>People In Membership</CardTitle>
            <CounterContainer>
              <CounterGroup>
                <CounterLabel>Adults (Over 10 Years)</CounterLabel>
                <CounterControls>
                  <CounterButton onClick={() => decreaseCount("adults")}>
                    -
                  </CounterButton>
                  <CounterValue>{adults}</CounterValue>
                  <CounterButton onClick={() => increaseCount("adults")}>
                    +
                  </CounterButton>
                </CounterControls>
              </CounterGroup>

              <CounterGroup>
                <CounterLabel>Kids</CounterLabel>
                <CounterControls>
                  <CounterButton onClick={() => decreaseCount("kids")}>
                    -
                  </CounterButton>
                  <CounterValue>{kids}</CounterValue>
                  <CounterButton onClick={() => increaseCount("kids")}>
                    +
                  </CounterButton>
                </CounterControls>
              </CounterGroup>
            </CounterContainer>
          </Card>

          <Card>
            <CardTitle>Personal Details</CardTitle>
            {people.map((person, index) => {
              const isKid = index >= adults;
              if (kids === 0 && isKid) return null;

              return (
                <InputGroup key={index}>
                  <Input
                    type="text"
                    placeholder={`Person ${index + 1} Name (${
                      index < adults ? "Adult" : "Kid"
                    })`}
                    value={person.name}
                    onChange={(e) => {
                      const updatedPeople = [...people];
                      updatedPeople[index].name = e.target.value;
                      setPeople(updatedPeople);
                    }}
                  />
                  {errors[`person${index}`] && (
                    <ErrorText>{errors[`person${index}`]}</ErrorText>
                  )}

                  <Input
                    type="date"
                    value={person.dob}
                    onChange={(e) => handleDateChange(index, e.target.value)}
                  />
                  {errors[`personDob${index}`] && (
                    <ErrorText>{errors[`personDob${index}`]}</ErrorText>
                  )}
                </InputGroup>
              );
            })}
          </Card>

          <Card>
            <CardTitle>Nominees</CardTitle>
            <CounterGroup>
              <CounterLabel>Number of Nominees</CounterLabel>
              <CounterControls>
                <CounterButton onClick={() => decreaseCount("nominees")}>
                  -
                </CounterButton>
                <CounterValue>{nominees.length}</CounterValue>
                <CounterButton onClick={() => increaseCount("nominees")}>
                  +
                </CounterButton>
              </CounterControls>
            </CounterGroup>
            {nominees.map((nominee, index) => (
              <InputGroup key={index}>
                <Input
                  type="text"
                  placeholder={`Nominee ${index + 1} Name`}
                  value={nominee.name}
                  onChange={(e) => {
                    const updatedNominees = [...nominees];
                    updatedNominees[index].name = e.target.value;
                    setNominees(updatedNominees);
                  }}
                />
                {errors[`nominee${index}`] && (
                  <ErrorText>{errors[`nominee${index}`]}</ErrorText>
                )}
              </InputGroup>
            ))}
          </Card>

          <Card>
            <CardTitle>
              Select Locations ({selectedLocations.length}/{maxLocations})
            </CardTitle>

            <SelectedLocationsContainer>
              {selectedLocations.map((loc) => (
                <LocationTag key={loc.locId}>
                  {loc.location}
                  <RemoveButton onClick={() => removeLocation(loc.locId)}>
                    ×
                  </RemoveButton>
                </LocationTag>
              ))}
            </SelectedLocationsContainer>

            <LocationGrid>
              {availableLocations.map((loc) => (
                <LocationChip
                  key={loc.locId}
                  onClick={() => {
                    if (selectedLocations.length < maxLocations) {
                      handleLocationSelect({ target: { value: loc.locId } });
                    }
                  }}
                  disabled={selectedLocations.length >= maxLocations}
                >
                  {loc.location}
                </LocationChip>
              ))}
            </LocationGrid>
            {errors.locations && <ErrorText>{errors.locations}</ErrorText>}
          </Card>

          <ProceedButton onClick={handleProceed}>
            Proceed to Payment
          </ProceedButton>
        </ContentWrapper>
      </ScrollContainer>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
  margin: 15vh 0 0 0;
`;

const ScrollContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PriceCard = styled(Card)`
  text-align: center;
  background-color: #441B77;
  color: white;
`;

const PriceTitle = styled.h2`
  font-family: "Poppins-Bold", sans-serif;
  font-size: 24px;
  margin-bottom: 8px;
`;

const PriceSubtext = styled.p`
  font-family: "Poppins-Regular", sans-serif;
  font-size: 16px;
  margin-bottom: 8px;
`;

const MaxMembersText = styled.p`
  font-family: "Poppins-Medium", sans-serif;
  font-size: 14px;
  opacity: 0.9;
`;

const CardTitle = styled.h3`
  font-family: "Poppins-SemiBold", sans-serif;
  font-size: 18px;
  color: #441B77;
  margin-bottom: 16px;
`;

const CounterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CounterGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CounterLabel = styled.span`
  font-family: "Poppins-Medium", sans-serif;
  color: #333;
`;

const CounterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CounterButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: #441B77;
  color: white;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #321355;
  }
`;

const CounterValue = styled.span`
  font-family: "Poppins-Medium", sans-serif;
  min-width: 24px;
  text-align: center;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: "Poppins-Regular", sans-serif;
  margin-bottom: 8px;

  &:focus {
    outline: none;
    border-color: #441B77;
  }
`;

const ErrorText = styled.p`
  color: red;
  font-size: 12px;
  margin-top: -4px;
  margin-bottom: 8px;
  font-family: "Poppins-Regular", sans-serif;
`;

const LocationGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

const LocationChip = styled.div`
  padding: 8px 16px;
  background-color: ${(props) => (props.disabled ? "#f5f5f5" : "white")};
  border: 1px solid ${(props) => (props.disabled ? "#ddd" : "#441B77")};
  border-radius: 20px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-family: "Poppins-Regular", sans-serif;
  font-size: 14px;
  color: ${(props) => (props.disabled ? "#999" : "#441B77")};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => (props.disabled ? "#f5f5f5" : "#441B77")};
    color: ${(props) => (props.disabled ? "#999" : "white")};
  }
`;

const SelectedLocationsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: ${(props) => (props.children?.length > 0 ? "16px" : "0")};
`;

const LocationTag = styled.div`
  display: flex;
  align-items: center;
  background-color: #441B77;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-family: "Poppins-Regular", sans-serif;
  font-size: 14px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: white;
  margin-left: 8px;
  cursor: pointer;
  font-size: 18px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

const ProceedButton = styled.button`
  width: 100%;
  padding: 16px;
  background-color: #441B77;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins-SemiBold", sans-serif;
  font-size: 16px;
  cursor: pointer;
  margin-top: 16px;

  &:hover {
    background-color: #321355;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

export default PersonalDetails;
